import React from 'react'
import { timespanFromTimestamps } from './RefreshFunctions'
import { defaultConfigs } from '../utils/initialStates'

const isNumber = (val: any) => typeof val === "number" && val === val;

/**
 * This function creates an array and fills it with all the runs of the tree.
 *
 * @param formattedTree {interventionDataTypes} - the formattedTree of operations
 * @returns {runDataTypes[]} an array with all the runs of the tree
 */

export const getRuns = (formattedTree: Array<interventionDataTypes>): runDataTypes[] => {
    let runs: runDataTypes[] = []
    if (Array.isArray(formattedTree))
        formattedTree.forEach(intervention => {
            intervention.children.forEach(section => {
                section.children.forEach(run => {
                    runs.push(run)
                })
            })
        })
    return runs
}

/**
 * Gives priority to active interventions that began later
 *
 * @param a Intervention
 * @param b Intervention
 * @returns -1 (intervention to be prioritized) or 1
 */

const sortingInterventions = (a: TreeIntervention, b: TreeIntervention): number => {
    let res: number = 0
    if (a && b) {
        if (a.status === 'active' && b.status !== 'active') res = -1
        if (a.status !== 'active' && b.status === 'active') res = 1
        if ((a.status === 'active' && b.status === 'active') || (a.status !== 'active' && b.status !== 'active')) {
            if (a.begin === 0 && b.begin !== 0) res = 1
            if (a.begin !== 0 && b.begin === 0) res = -1
            if (a.begin !== 0 && b.begin !== 0) res = a.begin > b.begin ? -1 : 1
        }
    }
    return res
}

/**
 * This function responds a type of operation based on the description of an operation
 *
 * @param operationDescription {string} - the description of an operation
 * @returns {String} - the type of operation
 */

const findOperation = (operationDescription: string): string => {
    switch (operationDescription) {
        case (operationDescription.match(/drilling ahead rot/) || {}).input:
            return 'Drilling ahead rot'
            break
        case (operationDescription.match(/sliding drilling/) || {}).input:
            return 'Sliding Drilling'
            break
        case (operationDescription.match(/quasi running in hole/) || {}).input:
            return 'Quasi running in hole'
            break
        case (operationDescription.match(/quasi pulling out of hole/) || {}).input:
            return 'Quasi pulling out of hole'
            break
        case (operationDescription.match(/pumping in hole/) || {}).input:
            return 'Pumping in hole'
            break
        case (operationDescription.match(/pumping out of hole/) || {}).input:
            return 'Pumping out of hole'
            break
        case (operationDescription.match(/back reaming/) || {}).input:
            return 'Back Reaming'
            break
        case (operationDescription.match(/reaming down/) || {}).input:
            return 'Reaming down'
            break
        case (operationDescription.match(/back reaming/) || {}).input:
            return 'Back reaming'
            break
        case (operationDescription.match(/tripping in|Trip in|Trip In|TRIP IN/) || {}).input:
            return 'Tripping in'
            break
        case (operationDescription.match(/Trip Out|TRIP OUT/) || {}).input:
            return 'Tripping out'
            break
        case (operationDescription.match(/full static not circulating not reciprocating/) || {}).input:
            return 'Full static: not circulating, not reciprocating'
            break
        case (operationDescription.match(/circulating off bottom static/) || {}).input:
            return 'Circulating off bottom (static)'
            break
        case (operationDescription.match(/rotating off bottom static/) || {}).input:
            return 'Rotating off bottom (static)'
            break
        case (operationDescription.match(/circulating rotating off bottom static/) || {}).input:
            return 'Circulating rotating off bottom (static)'
            break
        case (operationDescription.match(/circulating on bottom static/) || {}).input:
            return 'Circulating on bottom (static)'
            break
        case (operationDescription.match(/rotating on bottom static/) || {}).input:
            return 'Rotating on bottom (static)'
            break
        case (operationDescription.match(/circulating rotating on bottom/) || {}).input:
            return 'Circulating rotating on bottom'
            break
        case (operationDescription.match(/circulating rotating off bottom reciprocating/) || {}).input:
            return 'Circulating rotating off bottom reciprocating'
            break
        case (operationDescription.match(/reciprocrating off bottom/) || {}).input:
            return 'Reciprocrating off bottom'
            break
        case (operationDescription.match(/drilling|Drilling|$DRILL /) || {}).input:
            return 'Drilling connection'
            break
        case (operationDescription.match(/tripping connection/) || {}).input:
            return 'Tripping connection'
            break
        case (operationDescription.match(/casing running|CASE/) || {}).input:
            return 'Casing running'
            break
        case (operationDescription.match(/connection/) || {}).input:
            return 'Connection'
            break
        default:
            return 'Unknown'
            break
    }
}

/**
 * This function creates a new tree based on the tree received on the backend. This new tree has the structure that is used to display the tree
 *
 * @param inputTree {UnformattedTreeProps} - tree receveid from the backend
 * @returns {interventionDataTypes[]} - formatted tree
 */

export const formatTree = (
    inputTree: UnformattedTreeProps,
    OperationsPickerConfigs: OperationsPickerConfigsProps
): Array<interventionDataTypes> => {
    const originalTree = inputTree?.interventions
    let outputTree = new Array<interventionDataTypes>()

    if (originalTree) {
        originalTree
            .sort((a, b) => sortingInterventions(a, b))
            .filter(intervention =>
                filteringInterventions(intervention, OperationsPickerConfigs.onlyDisplayRunsWithOperations)
            )
            .forEach((intervention, i) => {
                let keyIntervention = intervention.id
                outputTree.push(interventionFormatter(intervention, keyIntervention))
                let start_md_unit = intervention.units.start_md ?? 'm'
                let end_md_unit = intervention.units.final_md ?? 'm'
                intervention.sections
                    .filter(section =>
                        filteringSections(section, OperationsPickerConfigs.onlyDisplayRunsWithOperations)
                    )
                    .forEach((section, j) => {
                        let keySection = keyIntervention + ',' + section.index
                        outputTree[i].children.push(sectionFormatter(section.bitDiameter, keySection))
                        section.runs
                            .filter(run => filteringRuns(run, OperationsPickerConfigs.onlyDisplayRunsWithOperations))
                            .forEach((run, k) => {
                                let runLabel = 'Run ' + (run.name ?? '')
                                let keyRun = keySection + ',' + run.index
                                let runType = run.type
                                let runReference = {
                                    interventionId: intervention.id,
                                    sectionId: section.id,
                                    runId: run.id,
                                    drillingSectionPosIndex: section.index,
                                    runPosIndex: run.index
                                }

                                outputTree[i].children[j].children.push(
                                    runFormatter(runType, runLabel, keyRun, runReference)
                                )
                                if (OperationsPickerConfigs.displayOperations) {
                                    let m = -1

                                    if (run.operations !== undefined) {
                                        run.operations.forEach(operation => {
                                            m++
                                            buildOperation(
                                                outputTree[i].children[j].children[k],
                                                operation,
                                                runReference,
                                                start_md_unit,
                                                end_md_unit,
                                                keyRun,
                                                m
                                            )
                                        })
                                    }

                                    if (run.casingOperations !== undefined) {
                                        run.casingOperations.forEach(operation => {
                                            m++
                                            buildOperation(
                                                outputTree[i].children[j].children[k],
                                                operation,
                                                runReference,
                                                start_md_unit,
                                                end_md_unit,
                                                keyRun,
                                                m
                                            )
                                        })
                                    }

                                    updateRunTitleWithTimestamp(outputTree[i].children[j].children[k], runLabel)
                                }
                            })
                    })
            })
    }
    return outputTree
}

/**
 * This function builds and pushes the object of an operation {operationNodesTypes} into its run
 *
 * @param run {runDataTypes}
 * @param operation {TreeOperation}
 * @param runReference {runReferenceProps}
 * @param start_md_unit {string}
 * @param end_md_unit {string}
 * @param keyRun {string}
 * @param m {number}
 * @returns {void}
 */

const buildOperation = (
    run: runDataTypes,
    operation: TreeOperation,
    runReference: runReferenceProps,
    start_md_unit: string,
    end_md_unit: string,
    keyRun: string,
    m: number
): void => {
    run.children.push(
        operationFormatter(
            operation,
            runReference,
            start_md_unit,
            end_md_unit,
            keyRun,
            m,
            runTitlesTitleFormatter(runReference)
        )
    )
    addsTimestamp('start_timestamp', run, m, operation)
    addsTimestamp('end_timestamp', run, m, operation)
    updateOperationTitlesTitleWithTimestamp(run.children[m], start_md_unit, end_md_unit)
}

/**
 * This function adds a timestamp to the given operation, and checks if the run timestamp is adequate to its operations' timestamps
 *
 * @param typeTimestamp {"start_timestamp" | "end_timestamp"} - name of the property to be added
 * @param section {runDataTypes}
 * @param operationId {number}
 * @param operation {TreeOperation}
 * @returns void
 */

const addsTimestamp = (
    typeTimestamp: 'start_timestamp' | 'end_timestamp',
    section: runDataTypes,
    operationId: number,
    operation: TreeOperation
): void => {
    if (operation[typeTimestamp] !== undefined) {
        section.children[operationId][typeTimestamp] = operation[typeTimestamp]
        if (!section[typeTimestamp]) {
            section[typeTimestamp] = operation[typeTimestamp]
        } else if (
            (typeTimestamp === 'end_timestamp' && section[typeTimestamp] < operation[typeTimestamp]) ||
            (typeTimestamp === 'start_timestamp' && section[typeTimestamp] > operation[typeTimestamp])
        ) {
            section[typeTimestamp] = operation[typeTimestamp]
        }
    }
}

/**
 * This function returns a title on the JSX format that is displayed by the tree
 *
 * @param name {string}
 * @param start_md {number}
 * @param start_md_unit {string}
 * @param end_md {number}
 * @param end_md_unit {string}
 * @returns {JSX.Element}
 */

const titleOperationFormatter = (
    name: string,
    start_md: number,
    start_md_unit: string,
    end_md: number,
    end_md_unit: string,
    titlesTitle: string
): JSX.Element => (
    <div className="tree-line" title={titlesTitle}>
        <span className="opMode">{name}</span>
        {isNumber(start_md) && isNumber(end_md) && (
            <span className="depth-Op-Mode">{start_md.toFixed(2) + start_md_unit + ' to ' + end_md.toFixed(2) + end_md_unit}</span>
        )}
        <span className="using-for-model">Using for model</span>
        <span className="use-for-model">Use for model</span>
    </div>
)

/**
 * This function updates the title of the property title of the object operation with the timestamp, if there is one
 *
 * @param operation {operationNodesTypes}
 * @param start_md_unit {string}
 * @param end_md_unit {string}
 */

const updateOperationTitlesTitleWithTimestamp = (operation: operationNodesTypes, start_md_unit, end_md_unit): void => {
    if (operation.start_timestamp !== undefined) {
        let titlesTitle = titlesTitleFormatterWithTimestamps(
            operation.start_timestamp,
            operation.end_timestamp,
            operation.runReference
        )
        operation.title = titleOperationFormatter(
            operation.label,
            operation.start_md,
            start_md_unit,
            operation.end_md,
            end_md_unit,
            titlesTitle
        )
    }
}

/**
 * This function returns a string containing the timespan and the run reference path of the operation
 *
 * @param operation {operationNodesTypes}
 * @returns {string}
 */

const titlesTitleFormatterWithTimestamps = (
    start_timestamp: number,
    end_timestamp: number,
    runReference: runReferenceProps
): string =>
    `Timespan: ${timespanFromTimestamps(start_timestamp, end_timestamp, false)}\n${runTitlesTitleFormatter(
        runReference
    )}`

/**
 * This function returns an object that correspond to an operation on the structure used on the displayed tree
 *
 * @param operation {TreeOperation} - the input operation
 * @param runReference {runReferenceProps}
 * @param start_md_unit {number}
 * @param end_md_unit {number}
 * @param keyRun {string}
 * @param m {number}
 * @returns {operationNodesTypes}
 */

const operationFormatter = (
    operation: TreeOperation,
    runReference: runReferenceProps,
    start_md_unit: string,
    end_md_unit: string,
    keyRun: string,
    m: number,
    runTitlesTitle: string
): operationNodesTypes => {
    return {
        title: titleOperationFormatter(
            operation.name,
            operation.start_md,
            start_md_unit,
            operation.end_md,
            end_md_unit,
            runTitlesTitle
        ),
        key: 'Operation (' + keyRun + ',' + m + ')',
        label: operation.name,
        start_md: operation.start_md,
        end_md: operation.end_md,
        operation: findOperation(operation.name),
        runReference: runReference
    }
}

/**
 * This function returns an object that correspond to a run on the structure used on the displayed tree
 *
 * @param runLabel {string}
 * @param keyRun {string}
 * @param runReference {runReferenceProps}
 * @returns {runDataTypes}
 */

const runFormatter = (
    runType: string,
    runLabel: string,
    keyRun: string,
    runReference: runReferenceProps
): runDataTypes => {
    return {
        title: (
            <div className="tree-line" title={runTitlesTitleFormatter(runReference)}>
                <span className="opMode">{runLabel + ' '}</span>
                <span className="using-for-model">Using for model</span>
                <span className="use-for-model">Use for model</span>
            </div>
        ),
        label: runLabel,
        key: 'Run (' + keyRun + ')',
        children: [],
        runReference: runReference,
        type: runType
    }
}

/**
 * This function returns an object that correspond to a section on the structure used on the displayed tree
 *
 * @param bitDiameter {string}
 * @param keySection {string}
 * @returns {sectionDataTypes}
 */

const sectionFormatter = (bitDiameter: string, keySection: string): sectionDataTypes => {
    return {
        title: 'Section ' + (bitDiameter ? bitDiameter + "'" : ''),
        key: 'Section (' + keySection + ')',
        children: []
    }
}

/**
 * This function returns an object that correspond to an intervention on the structure used on the displayed tree
 *
 * @param intervention {TreeIntervention}
 * @param keyIntervention {string}
 * @returns {interventionDataTypes}
 */

const interventionFormatter = (intervention: TreeIntervention, keyIntervention: string): interventionDataTypes => {
    return {
        id: intervention.id,
        title:
            'Intervention ' +
            intervention.id +
            ' (' +
            (intervention.name ? intervention.name + ' - ' : '') +
            intervention.status +
            ')',
        key: 'Intervention (' + keyIntervention + ')',
        children: []
    }
}

/**
 * This function checks if the run has timestamps and if so, it updates the title of the run
 *
 * @param run {TreeRun}
 * @param runLabel {string}
 * @returns {void}
 */

const updateRunTitleWithTimestamp = (run: runDataTypes, runLabel: string) => {
    if (run.start_timestamp !== undefined) {
        let optionsDateFormat = {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        }
        let runTitlesTitle = titlesTitleFormatterWithTimestamps(
            run.start_timestamp,
            run.end_timestamp,
            run.runReference
        )
        if (run.end_timestamp !== undefined) {
            run.title = (
                <div className="tree-line" title={runTitlesTitle}>
                    <span className="opMode">{runLabel}</span>
                    <span className="depth-Op-Mode">
                        {new Date(run.start_timestamp).toLocaleDateString('en-US', optionsDateFormat) +
                            ' to ' +
                            new Date(run.end_timestamp).toLocaleDateString('en-US', optionsDateFormat)}
                    </span>
                    <span className="using-for-model">Using for model</span>
                    <span className="use-for-model">Use for model</span>
                </div>
            )
        } else {
            run.title = (
                <div className="tree-line" title={runTitlesTitle}>
                    <span className="opMode">{runLabel}</span>
                    <span className="depth-Op-Mode">
                        {new Date(run.start_timestamp).toLocaleDateString('en-US', optionsDateFormat)}
                    </span>
                    <span className="using-for-model">Using for model</span>
                    <span className="use-for-model">Use for model</span>
                </div>
            )
        }
    }
}

/**
 * This function returns a string that disclaims the run reference
 *
 * @param runReference {runReferenceProps}
 * @returns {string}
 */

const runTitlesTitleFormatter = (runReference: runReferenceProps) => `Run reference: ${JSON.stringify(runReference)}`

/**
 * This function checks if the runs will be filtered according to the span picker configurations and if the run carries operations
 *
 * @param run {TreeRun}
 * @param onlyDisplayRunsWithOperations {boolean}
 * @returns {boolean}
 */

const filteringRuns = (run: TreeRun, onlyDisplayRunsWithOperations: boolean): boolean => {
    let res = true
    if (onlyDisplayRunsWithOperations) {
        res = run.operations?.length > 0 || run.casingOperations?.length > 0
    }
    return res
}

/**
 * This function checks if the sections have runs with operations and if the runs without operations whould be displayed
 * (if not, the section that only have runs without operations wont be displayed too)
 *
 * @param section {TreeSection}
 * @param onlyDisplayRunsWithOperations {boolean}
 * @returns {boolean}
 */

const filteringSections = (section: TreeSection, onlyDisplayRunsWithOperations: boolean): boolean => {
    let res = section.runs?.length > 0
    if (onlyDisplayRunsWithOperations && res) {
        res = false
        section.runs.every(run => {
            if (run.operations?.length > 0 || run.casingOperations?.length > 0) {
                res = true
                return false
            }
            return true
        })
    }
    return res
}

/**
 * This function checks if the interevention have sections with runs with operations and if the runs without operations whould be displayed
 * (if not, the intervention with section that only have runs without operations wont be displayed too)
 *
 * @param intervention {TreeIntervention}
 * @param onlyDisplayRunsWithOperations {boolean}
 * @returns {boolean}
 */

const filteringInterventions = (intervention: TreeIntervention, onlyDisplayRunsWithOperations: boolean): boolean => {
    let res = intervention.sections?.length > 0
    if (onlyDisplayRunsWithOperations && res) {
        res = false
        intervention.sections.every(section => {
            section.runs.every(run => {
                if (run.operations?.length > 0 || run.casingOperations?.length > 0) {
                    res = true
                    return false
                }
                return true
            })
            if (res) {
                return false
            } else {
                return true
            }
        })
    }
    return res
}

/**
 * This functions checks if the configurations from Live has all the properties, not having anyone, it will get the default values on initialConfigs
 * @param inputConfigs {OperationsPickerConfigsProps}
 * @param initialConfigs {OperationsPickerConfigsProps}
 * @returns {OperationsPickerConfigsProps}
 */

export const checkingConfigs = (
    inputConfigs: OperationsPickerConfigsProps,
    defaultConfigs: OperationsPickerConfigsProps
): OperationsPickerConfigsProps => {
    let res = {}
    Object.keys(defaultConfigs).forEach(e => {
        res[e] = inputConfigs && inputConfigs[e] !== undefined ? inputConfigs[e] : defaultConfigs[e]
    })
    return res
}

export const checkingConfigsParams = (
    paramsConfig: OperationsPickerConfigsProps,
    inputConfigs: OperationsPickerConfigsProps
): OperationsPickerConfigsProps => {
    let res = defaultConfigs

    Object.keys(defaultConfigs).forEach(e => {
        if (paramsConfig[e])
            res[e] = typeof defaultConfigs[e] == 'boolean' ? JSON.parse(paramsConfig[e]) : paramsConfig[e]
        else
            res[e] =
                inputConfigs && inputConfigs[e] !== undefined
                    ? inputConfigs[e]
                    : inputConfigs[e] == undefined
                    ? defaultConfigs[e]
                    : JSON.parse(inputConfigs[e])
    })

    return res
}

export const stringToDateTime = (inputString: string) => {
    const regex = /(\d{4}-\d{2}-\d{2})(?: (\d{2}:\d{2}))?(?: to (\d{4}-\d{2}-\d{2})(?: (\d{2}:\d{2}))?)?/
    const matches = inputString.match(regex)

    const startDate = matches?.[1]
    const startTime = matches?.[2]
    const endDate = matches?.[3]
    const endTime = matches?.[4]

    return [startDate, startTime, endDate, endTime]
}

export const parseOptions = value => {
    if (typeof value === 'string') {
        try {
            return JSON.parse(value)
        } catch (error) {}
    }
    return value
}

export const getRunTypeOptions = (values: string) => {
    if (values?.length > 1) {
        return values?.split(',')?.map(d => ({ value: d, label: d }))
    }
    return []
}
